/**
 * Reading word-timing helpers — EAA-1755.
 *
 * Shared, framework-agnostic utilities for karaoke-style word highlighting:
 * tokenising a passage into highlightable words and resolving which word is
 * active at a given audio time. Used by `SyncedPassage` (content-kit) and the
 * `InteractiveAudioReader` element (content-elements); available to any content
 * type that needs synced reading.
 */

/** One word boundary in synthesised audio, in seconds from the start. */
export interface ReadingWordTimestamp {
    word: string;
    /** Optional normalised key for the word, e.g. "hello". */
    slug?: string;
    start: number;
    end: number;
}

/** A rendered token: either a spoken word (highlightable) or a separator. */
export interface ReadingToken {
    text: string;
    /** Whitespace/separator tokens are not highlightable. */
    isWord: boolean;
    /** First timestamp index this token maps to (only meaningful when isWord). */
    wordIndex: number;
    /**
     * Last timestamp index this token maps to. Equals `wordIndex` for a normal
     * 1:1 word; spans a range when the synthesizer split one display word into
     * several timestamps (e.g. "co-located" → "co" + "located").
     */
    wordEndIndex: number;
}

/**
 * Tokenise a passage into words + separators, assigning each word token the
 * next timestamp by order. TTS word timing aligns to whitespace-delimited
 * words, so index alignment holds even when punctuation is attached
 * (e.g. "Hello,").
 */
export function tokenizeReadingText(text: string): ReadingToken[] {
    const tokens: ReadingToken[] = [];
    if (!text) return tokens;

    const parts = text.match(/\s+|[^\s]+/g) ?? [];
    let wordIndex = 0;
    for (const part of parts) {
        const isWord = /\S/.test(part);
        tokens.push({
            text: part,
            isWord,
            wordIndex: isWord ? wordIndex : -1,
            wordEndIndex: isWord ? wordIndex : -1,
        });
        if (isWord) wordIndex += 1;
    }
    return tokens;
}

/** Normalise a word for alignment: lowercase, alphanumerics only. */
function normalizeAlnum(word: string): string {
    return (word ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}

/**
 * EAA-1755: Tokenise a passage and align each display word to the AI agent's
 * `word_timestamps`, which may segment differently than whitespace splitting.
 *
 * The agent can split one display word into several timestamps (hyphenated
 * compounds like "co-located" → "co" + "located"), merge/reflow short words, or
 * voice a symbol as a word ("+" → "plus"). Naive by-order indexing drifts after
 * any such word, breaking both highlight sync and click-to-seek.
 *
 * We align on the actual data: build a normalised character stream of the spoken
 * words (remembering which timestamp index each character came from), then locate
 * each display word in that stream from a forward-moving pointer. Because it is
 * character-based and always searches in order, it stays correct through splits,
 * merges, and repeated words — every token gets the right [wordIndex,
 * wordEndIndex] range. Falls back to naive tokenisation when no timestamps are
 * supplied (e.g. browser-TTS mode).
 */
export function alignReadingTokens(
    text: string,
    timestamps: ReadingWordTimestamp[] | undefined
): ReadingToken[] {
    const tokens = tokenizeReadingText(text);
    if (!timestamps?.length) return tokens;

    // Normalised char stream of spoken words + a per-char map back to the
    // timestamp index that produced it.
    let stream = '';
    const charToTs: number[] = [];
    for (let i = 0; i < timestamps.length; i += 1) {
        const norm = normalizeAlnum(timestamps[i].word);
        for (let c = 0; c < norm.length; c += 1) {
            stream += norm[c];
            charToTs.push(i);
        }
    }
    if (stream.length === 0) return tokens;

    const lastChar = charToTs.length - 1;
    const tsAt = (p: number) => charToTs[Math.max(0, Math.min(p, lastChar))];
    let pos = 0;

    for (const tok of tokens) {
        if (!tok.isWord) continue;

        const target = normalizeAlnum(tok.text);

        // Pure-symbol token (e.g. "+", "—"): no letters to match. The agent may
        // voice it as a word occupying the stream here; map to that timestamp
        // without consuming characters so real words still line up.
        if (target === '') {
            const idx = tsAt(pos);
            tok.wordIndex = idx;
            tok.wordEndIndex = idx;
            continue;
        }

        // Prefer a match exactly at the pointer; otherwise the next occurrence.
        const start = stream.startsWith(target, pos) ? pos : stream.indexOf(target, pos);
        if (start < 0) {
            // Word not in the remaining stream (stale/edited text): best-effort,
            // pin to the current position without advancing the pointer.
            const idx = tsAt(pos);
            tok.wordIndex = idx;
            tok.wordEndIndex = idx;
            continue;
        }
        const end = start + target.length - 1;
        tok.wordIndex = charToTs[start];
        tok.wordEndIndex = charToTs[end];
        pos = end + 1;
    }

    return tokens;
}

/**
 * Character offset (into the passage) where each word starts, in word order.
 * Used to map a Web Speech API `SpeechSynthesisUtterance` boundary `charIndex`
 * to a word index for browser-TTS highlighting.
 */
export function wordStartOffsets(text: string): number[] {
    const offsets: number[] = [];
    if (!text) return offsets;
    const parts = text.match(/\s+|[^\s]+/g) ?? [];
    let pos = 0;
    for (const part of parts) {
        if (/\S/.test(part)) offsets.push(pos);
        pos += part.length;
    }
    return offsets;
}

/**
 * Extra pause (seconds) a synthesizer typically takes after a word, based on its
 * trailing punctuation — sentence enders pause longest, clause marks less. Any
 * closing quotes/brackets/parens are stripped first so `Alex."` still counts as a
 * sentence end. Baseline (unscaled) values; scale with the char-time factor.
 */
export function punctuationPauseAfter(word: string): number {
    const trimmed = word.replace(/[)"'”’\]»]+$/u, '');
    const last = trimmed.slice(-1);
    if (/[.!?…]/u.test(last)) return 0.35;
    if (/[,;:—–]/u.test(last)) return 0.18;
    return 0;
}

/**
 * Estimated speak duration (seconds) per word, including a punctuation pause.
 * Used to drive TTS highlighting when the browser fires no onboundary events.
 * `secondsPerChar` encodes the speaking rate (e.g. 0.075 / utterance.rate); the
 * punctuation pause scales with it too, so faster speech pauses proportionally
 * less.
 */
export function estimateWordDurations(text: string, secondsPerChar = 0.075): number[] {
    const words = text.match(/\S+/g) ?? [];
    const pauseScale = secondsPerChar / 0.075;
    return words.map(
        (w) => (w.length + 1) * secondsPerChar + punctuationPauseAfter(w) * pauseScale
    );
}

/** Word index containing character offset `charIndex`, or -1. */
export function wordIndexForChar(offsets: number[], charIndex: number): number {
    let idx = -1;
    for (let i = 0; i < offsets.length; i += 1) {
        if (offsets[i] <= charIndex) idx = i;
        else break;
    }
    return idx;
}

/**
 * Index of the word active at time `t` (seconds), or -1 if none. Uses the
 * word's [start, end) window; falls back to the last word whose start has
 * passed so highlighting never stalls between reported boundaries.
 */
export function activeWordIndexAt(timestamps: ReadingWordTimestamp[], t: number): number {
    if (!timestamps?.length) return -1;
    let candidate = -1;
    for (let i = 0; i < timestamps.length; i += 1) {
        const w = timestamps[i];
        if (t >= w.start && t < w.end) return i;
        if (t >= w.start) candidate = i;
        else break;
    }
    return candidate;
}
